import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { getReadylist, addReadylistTicker, deleteReadylistTicker, createOrder, getAccount, getAccountTickers, getAccountOrders, downgradeToWatchlist, updateAccountTickerTheme } from '../services/api';
import './Watchlist.css';
import { Button, Modal } from 'react-bootstrap';
import OrderDialog from '../components/OrderDialog';
import { getAllAccounts } from '../services/api';
import { updateReadylistItem } from '../services/api';
import { ThemeContext } from '../contexts/ThemeContext';
import TickerNotesPanel from '../components/TickerNotesPanel';
import ReadylistRow from '../components/ReadylistRow';
import { MAX_TICKER_LIST_SIZE } from '../utils/listLimits';
import useQuickBuy from '../hooks/useQuickBuy';

const Readylist = () => {
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme,
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';

  
  const [readylist, setReadylist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tickerQuery, setTickerQuery] = useState('');
  const [addError, setAddError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [removingRowIds, setRemovingRowIds] = useState([]);
  const [deleteError, setDeleteError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderTicker, setOrderTicker] = useState(null);
  const [orderAccountId, setOrderAccountId] = useState('');
  const [orderDialogLoading, setOrderDialogLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountTickers, setSelectedAccountTickers] = useState([]);
  const [selectedAccountOrders, setSelectedAccountOrders] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTicker, setDeleteTicker] = useState(null);
  const [sortColumn, setSortColumn] = useState(() => {
    const saved = localStorage.getItem('readylist_sortColumn');
    return saved || 'status';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem('readylist_sortDirection');
    return saved || 'asc';
  });
  const [pinnedTickerId, setPinnedTickerId] = useState(null);
  const [themeToggleLoadingId, setThemeToggleLoadingId] = useState(null);

  const removalTimersRef = useRef({});
  const ROW_REMOVE_ANIMATION_DURATION = 260;

  // Error states
  const [downgradeError, setDowngradeError] = useState('');
  const [priceUpdateError, setPriceUpdateError] = useState('');
  const maxReadylistSize = MAX_TICKER_LIST_SIZE;

  const focusTickerBySymbol = (symbol, sourceItems = readylist) => {
    if (!symbol) return;
    const normalized = symbol.trim().toUpperCase();
    const target = sourceItems.find(item => item.ticker?.toUpperCase() === normalized);
    if (target) {
      setPinnedTickerId(target.id);
      setSelectedId(target.id);
      setHighlightedRowId(target.id);
    }
  };

  const dispatchReadylistBadge = useCallback((count) => {
    try { localStorage.setItem('readylist_count', String(count)); } catch (e) {}
    window.dispatchEvent(new CustomEvent('readylist:badge', { detail: { count } }));
  }, []);

  const fetchReadylist = async ({ focusTicker } = {}) => {
    setLoading(true);
    setPriceUpdateError('');
    try {
      const res = await getReadylist();
      let items = res.data || [];

      const itemsNeedingUpdate = items.filter(
        item => item.include_in_price_updates === false
      );

      if (itemsNeedingUpdate.length > 0) {
        try {
          await Promise.all(
            itemsNeedingUpdate.map(item =>
              updateReadylistItem(item.id, { include_in_price_updates: true })
            )
          );
        } catch (enforceError) {
          console.error('Error enforcing price updates for readylist items:', enforceError);
          setPriceUpdateError(
            enforceError.response?.data?.error ||
            'Failed to enforce price updates for readylist items.'
          );
        }
      }

      items = items.map(item => ({
        ...item,
        include_in_price_updates: true
      }));

      setReadylist(items);
      dispatchReadylistBadge(items.length);
      if (focusTicker) {
        focusTickerBySymbol(focusTicker, items);
      } else if (items.length > 0 && !selectedId) {
        setSelectedId(items[0].id);
      }
    } catch (err) {
      setReadylist([]);
      dispatchReadylistBadge(0);
      setPriceUpdateError(err.response?.data?.error || 'Failed to load readylist data.');
    }
    setLoading(false);
  };

  const { openQuickBuy, quickBuyModals } = useQuickBuy({
    accounts,
    onOrderCreated: fetchReadylist,
    storageKey: 'readylist_quick_buy_default_fraction',
    sizingMode: 'risk'
  });

  useEffect(() => {
    fetchReadylist();
    getAllAccounts().then(res => setAccounts(res.data)).catch(() => setAccounts([]));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const timers = removalTimersRef.current;
    return () => {
      Object.values(timers).forEach(timerId => clearTimeout(timerId));
    };
  }, []);

  const handleAddTicker = async (e) => {
    e.preventDefault();
    if (readylist.length >= maxReadylistSize) {
      setAddError(`You cannot have more than ${maxReadylistSize} tickers in your readylist.`);
      return;
    }
    const normalizedTicker = tickerQuery.trim().toUpperCase();
    if (!normalizedTicker) return;
    setAddError('');
    try {
      await addReadylistTicker(normalizedTicker);
      fetchReadylist({ focusTicker: normalizedTicker });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to add ticker';
      setAddError(errorMessage);
      if (errorMessage === 'Ticker already in readylist') {
        focusTickerBySymbol(normalizedTicker);
      }
    }
  };

  const handleRemove = async (id) => {
    const tickerToDelete = readylist.find(item => item.id === id);
    if (!tickerToDelete) return;

    const previousReadylist = readylist;
    const wasSelected = selectedId === id;

    setDeleteError('');
    setDeletingId(id);
    setRemovingRowIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );

    const finalizeRemoval = () => {
      setReadylist(prev => prev.filter(item => item.id !== id));
      if (wasSelected) {
        setSelectedId(null);
      }
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(current => (current === id ? null : current));
    };

    removalTimersRef.current[id] = setTimeout(() => {
      finalizeRemoval();
      delete removalTimersRef.current[id];
    }, ROW_REMOVE_ANIMATION_DURATION);

    try {
      await deleteReadylistTicker(id);
    } catch (error) {
      console.error('Failed to delete readylist ticker:', error);
      clearTimeout(removalTimersRef.current[id]);
      delete removalTimersRef.current[id];
      setReadylist(previousReadylist);
      if (wasSelected) {
        setSelectedId(id);
      }
      setDeleteError(
        error.response?.data?.error || 'Failed to delete ticker from readylist.'
      );
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDowngrade = async (id) => {
    const tickerToDowngrade = readylist.find(item => item.id === id);
    if (!tickerToDowngrade) return;

    const previousReadylist = readylist;
    const wasSelected = selectedId === id;

    setDowngradeError('');
    setDeletingId(id);
    setRemovingRowIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );

    const finalizeRemoval = () => {
      setReadylist(prev => prev.filter(item => item.id !== id));
      if (wasSelected) {
        setSelectedId(null);
      }
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(current => (current === id ? null : current));
    };

    removalTimersRef.current[id] = setTimeout(() => {
      finalizeRemoval();
      delete removalTimersRef.current[id];
    }, ROW_REMOVE_ANIMATION_DURATION);

    try {
      await downgradeToWatchlist(id);
    } catch (error) {
      console.error('Error downgrading ticker:', error);
      clearTimeout(removalTimersRef.current[id]);
      delete removalTimersRef.current[id];
      setReadylist(previousReadylist);
      if (wasSelected) {
        setSelectedId(id);
      }
      const errorMessage = error.response?.data?.error || 'Failed to downgrade ticker';
      setDowngradeError(errorMessage);
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleThemeToggle = async (item) => {
    if (!item || !item.ticker_id) {
      return;
    }
    const accountsForTheme = item.theme_accounts || [];
    if (accountsForTheme.length !== 1) {
      return;
    }

    const targetAccount = accountsForTheme[0];
    if (!targetAccount.account_id) {
      return;
    }

    const nextValue = !targetAccount.theme_enabled;

    try {
      setThemeToggleLoadingId(item.id);
      await updateAccountTickerTheme(targetAccount.account_id, item.ticker_id, nextValue);
      setReadylist(prev => prev.map(existing => {
        if (existing.id !== item.id) {
          return existing;
        }

        const updatedAccounts = (existing.theme_accounts || []).map(account => (
          account.account_id === targetAccount.account_id
            ? { ...account, theme_enabled: nextValue }
            : account
        ));

        return {
          ...existing,
          theme_accounts: updatedAccounts,
          theme_enabled: updatedAccounts.some(account => account.theme_enabled)
        };
      }));
    } catch (error) {
      console.error('Failed to toggle theme for readylist item:', error);
    } finally {
      setThemeToggleLoadingId(null);
    }
  };

  const handleCategoryUpdate = (updatedItem) => {
    setReadylist((prev) => prev.map((item) => item.id === updatedItem.id ? { ...item, category_id: updatedItem.category_id, category: updatedItem.category } : item));
  };

  // Add rank to sort columns
  const handleSort = (column) => {
    let newDirection;
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      newDirection = 'asc';
      setSortDirection('asc');
    }

    // Persist to localStorage
    localStorage.setItem('readylist_sortColumn', column);
    localStorage.setItem('readylist_sortDirection', newDirection);
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Apply ticker filter
  const filteredReadylist = tickerQuery
    ? readylist.filter(item => item.ticker.toLowerCase().includes(tickerQuery.toLowerCase()))
    : readylist;

  // Auto-select ticker when filter results in exactly one match
  useEffect(() => {
    if (tickerQuery && filteredReadylist.length === 1) {
      setSelectedId(filteredReadylist[0].id);
    }
  }, [tickerQuery, filteredReadylist]);

  // Sort by rank for display (unless sorting by another column)
  const sortedReadylist = [...filteredReadylist].sort((a, b) => {
    let aValue, bValue;
    switch (sortColumn) {
      case 'ticker':
        aValue = a.ticker?.toUpperCase() || '';
        bValue = b.ticker?.toUpperCase() || '';
        break;
      case 'price':
        aValue = a.price ?? 0;
        bValue = b.price ?? 0;
        break;
      case 'gain':
        aValue = a.gain ?? 0;
        bValue = b.gain ?? 0;
        break;
      case 'gain_pct':
        aValue = a.gain_pct ?? 0;
        bValue = b.gain_pct ?? 0;
        break;
      case 'status':
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
        break;
      case 'category':
        aValue = a.category?.toUpperCase() || '';
        bValue = b.category?.toUpperCase() || '';
        break;
      case 'theme': {
        const getThemeValue = (item) => {
          if (typeof item.theme_enabled === 'boolean') {
            return item.theme_enabled ? 1 : 0;
          }
          return (item.theme_accounts || []).some(account => account.theme_enabled) ? 1 : 0;
        };
        aValue = getThemeValue(a);
        bValue = getThemeValue(b);
        break;
      }
      case 'notes':
        // Sort by latest note update time, nulls last
        if (!a.latest_note_updated_at && !b.latest_note_updated_at) return 0;
        if (!a.latest_note_updated_at) return 1;
        if (!b.latest_note_updated_at) return -1;
        aValue = new Date(a.latest_note_updated_at).getTime();
        bValue = new Date(b.latest_note_updated_at).getTime();
        break;
      default:
        // Default to status sorting if no specific column is set
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const prioritizedReadylist = useMemo(() => {
    if (!pinnedTickerId) {
      return sortedReadylist;
    }
    const targetIndex = sortedReadylist.findIndex(item => item.id === pinnedTickerId);
    if (targetIndex <= 0) {
      return sortedReadylist;
    }
    const targetItem = sortedReadylist[targetIndex];
    return [
      targetItem,
      ...sortedReadylist.slice(0, targetIndex),
      ...sortedReadylist.slice(targetIndex + 1)
    ];
  }, [sortedReadylist, pinnedTickerId]);

  const handleRowClick = (id) => {
    setSelectedId(id);
  };

  useEffect(() => {
    if (pinnedTickerId && !readylist.some(item => item.id === pinnedTickerId)) {
      setPinnedTickerId(null);
    }
  }, [readylist, pinnedTickerId]);

  const handleTickerClear = () => {
    setTickerQuery('');
    setPinnedTickerId(null);
    setAddError('');
  };

  // Create enhanced accounts array with full account data for the selected account
  const enhancedAccounts = accounts.map(acc => {
    if (acc.account_id === orderAccountId && selectedAccount) {
      return selectedAccount;
    }
    return acc;
  });

  const fetchAccountData = async (accountId) => {
    try {
      // Fetch account details, tickers, and orders
      const [accountRes, tickersRes, ordersRes] = await Promise.all([
        getAccount(accountId),
        getAccountTickers(accountId),
        getAccountOrders(accountId)
      ]);
      
      setSelectedAccount(accountRes.data);
      setSelectedAccountTickers(tickersRes.data);
      setSelectedAccountOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };

  const handleAccountChange = async (newAccountId) => {
    setOrderAccountId(newAccountId);
    if (newAccountId) {
      await fetchAccountData(newAccountId);
    } else {
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };
  const handleOrderDialogClose = () => {
    setShowOrderDialog(false);
    setOrderTicker(null);
    setOrderAccountId('');
    setSelectedAccount(null);
    setSelectedAccountTickers([]);
    setSelectedAccountOrders([]);
  };
  const handleOrderSave = async (orderData) => {
    setOrderDialogLoading(true);
    try {
      await createOrder(orderAccountId, orderData);
      setShowOrderDialog(false);
      setOrderTicker(null);
      setOrderAccountId('');
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
      // Optionally refresh data
    } catch (error) {
      console.error('Error creating order:', error);
      // You might want to show an error message to the user here
    } finally {
      setOrderDialogLoading(false);
    }
  };

  // Calculate available cash after existing orders
  const nonDeletedOrderTotal = selectedAccountOrders.filter(o => o.status !== 'DELETED' && o.status !== 'FULFILLED').reduce((sum, o) => sum + (Number(o.price) * Number(o.quantity)), 0);
  const availableCashAfterOrders = (selectedAccount?.cash || 0) - nonDeletedOrderTotal;
  const handleDeleteConfirm = async () => {
    if (deleteTicker) {
      const tickerToDelete = deleteTicker;
      const previousReadylist = readylist;
      const wasSelected = selectedId === tickerToDelete.id;

      setShowDeleteConfirm(false);
      setDeleteTicker(null);
      setDeletingId(tickerToDelete.id);
      setRemovingRowIds(prev =>
        prev.includes(tickerToDelete.id) ? prev : [...prev, tickerToDelete.id]
      );

      const finalizeRemoval = () => {
        setReadylist(prev => prev.filter(item => item.id !== tickerToDelete.id));
        if (wasSelected) {
          setSelectedId(null);
        }
        setRemovingRowIds(prev => prev.filter(id => id !== tickerToDelete.id));
        setHighlightedRowId(current => (current === tickerToDelete.id ? null : current));
      };

      removalTimersRef.current[tickerToDelete.id] = setTimeout(() => {
        finalizeRemoval();
        delete removalTimersRef.current[tickerToDelete.id];
      }, ROW_REMOVE_ANIMATION_DURATION);

      try {
        await deleteReadylistTicker(tickerToDelete.id);
      } catch (error) {
        console.error('Failed to delete readylist ticker:', error);
        clearTimeout(removalTimersRef.current[tickerToDelete.id]);
        delete removalTimersRef.current[tickerToDelete.id];
        setReadylist(previousReadylist);
        if (wasSelected) {
          setSelectedId(tickerToDelete.id);
        }
        setDeleteError(
          error.response?.data?.error || 'Failed to delete ticker from readylist.'
        );
        setRemovingRowIds(prev => prev.filter(id => id !== tickerToDelete.id));
        setHighlightedRowId(tickerToDelete.id);
      } finally {
        setDeletingId(null);
      }
    }
  };
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteTicker(null);
    setHighlightedRowId(null);
  };

  
  // Helper function to get ticker ID for a readylist item
  const getTickerIdForReadylistItem = (readylistItem) => {
    // The ticker_id is now included in the readylist item data from the backend
    return readylistItem.ticker_id;
  };


  // Get seasonal background color with transparency
  const getSeasonalBackground = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }

    const opacity = isDark ? 0.2 : 0.1; // Higher opacity for dark mode
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`; // Green
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`; // Blue
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`; // Orange
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.1)' : 'rgba(108, 117, 125, 0.1)'; // Gray
      default:
        return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
  };

  const getSeasonalRowTint = () => {
    const opacity = isDark ? 0.08 : 0.045;

    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return `rgba(0, 123, 255, ${opacity})`;
    }

    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`;
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`;
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`;
      case 'winter':
        return `rgba(108, 117, 125, ${opacity})`;
      default:
        return `rgba(0, 123, 255, ${opacity})`;
    }
  };

  const getActiveOwnedRowStyle = (isPurchased) => {
    if (!isPurchased) {
      return {};
    }
    const outline = isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.02)';
    return {
      background: getSeasonalRowTint(),
      boxShadow: `inset 0 0 0 1px ${outline}`
    };
  };

  return (
    <div className="container-xl" style={{ '--seasonal-bg': getSeasonalBackground() }}>
      <div className="watchlist-2col-layout">
        <div className="watchlist-table-col">
          <form className="watchlist-add-form" onSubmit={handleAddTicker}>
            <div className="watchlist-input-row">
              <div className="watchlist-input-stack">
                <div className="watchlist-input-wrapper">
                  <input
                    type="text"
                    className="watchlist-input"
                    placeholder="Filter or add ticker (e.g. AAPL)"
                    value={tickerQuery}
                    onChange={e => setTickerQuery(e.target.value)}
                    maxLength={16}
                  />
                </div>
                <button
                  type="button"
                  className="watchlist-action-btn watchlist-clear-btn"
                  onClick={handleTickerClear}
                  disabled={!tickerQuery}
                >
                  Clear
                </button>
                <button type="submit" className="watchlist-action-btn watchlist-add-btn" disabled={readylist.length >= maxReadylistSize}>Add</button>
              </div>
              <div className="watchlist-total-summary">
                TOTAL ({prioritizedReadylist.length}/{maxReadylistSize})
              </div>
            </div>
            {addError && <span className="watchlist-error">{addError}</span>}
            {downgradeError && <span className="watchlist-error">{downgradeError}</span>}
            {deleteError && <span className="watchlist-error">{deleteError}</span>}
            {priceUpdateError && <span className="watchlist-error">{priceUpdateError}</span>}
            {readylist.length >= maxReadylistSize && (
              <span className="watchlist-error">Maximum of {maxReadylistSize} tickers reached.</span>
            )}
          </form>
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('ticker')}>
                      Ticker{getSortIndicator('ticker')}
                    </th>
                    <th className="text-center" style={{ width: '50px' }}>Trade</th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '110px' }} onClick={() => handleSort('notes')}>
                      Notes{getSortIndicator('notes')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('price')}>
                      Price{getSortIndicator('price')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('gain_pct')}>
                      Gain %{getSortIndicator('gain_pct')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('status')}>
                      Status{getSortIndicator('status')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '172px' }} onClick={() => handleSort('category')}>
                      Category{getSortIndicator('category')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '80px' }} onClick={() => handleSort('theme')}>
                      Thm{getSortIndicator('theme')}
                    </th>
                    <th className="text-end" style={{ width: '130px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10}>Loading...</td></tr>
                  ) : prioritizedReadylist.length === 0 ? (
                    <tr><td colSpan={10}>No tickers in your readylist.</td></tr>
                  ) : (
                    <>
                      {prioritizedReadylist.map((item, index) => (
                        <ReadylistRow
                          key={item.id}
                          item={item}
                          index={index}
                          selected={selectedId === item.id}
                          highlighted={highlightedRowId === item.id}
                          removing={removingRowIds.includes(item.id)}
                          deleting={deletingId === item.id}
                          themeToggleLoading={themeToggleLoadingId === item.id}
                          extraRowStyle={getActiveOwnedRowStyle(item.is_purchased)}
                          onRowClick={handleRowClick}
                          onQuickBuy={openQuickBuy}
                          onCategoryUpdate={handleCategoryUpdate}
                          onThemeToggle={handleThemeToggle}
                          onDowngrade={handleDowngrade}
                          onRemove={handleRemove}
                        />
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteConfirm} onHide={handleDeleteCancel} centered>
            <Modal.Header closeButton>
              <Modal.Title>Delete {deleteTicker?.ticker} from Readylist?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to remove <b>{deleteTicker?.ticker}</b> from your readylist?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleDeleteCancel}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteConfirm} disabled={deletingId === deleteTicker?.id}>
                {deletingId === deleteTicker?.id ? 'Deleting...' : 'Delete'}
              </Button>
            </Modal.Footer>
          </Modal>
          {/* Order Dialog Modal */}
          {showOrderDialog && (
            <OrderDialog
              key={`order-dialog-${orderAccountId}`}
              show={showOrderDialog}
              onHide={handleOrderDialogClose}
              onSave={handleOrderSave}
              accounts={enhancedAccounts}
              initialAccountId={orderAccountId}
              setAccountId={handleAccountChange}
              ticker={orderTicker}
              tickers={selectedAccountTickers}
              availableTickers={orderTicker ? [{ symbol: orderTicker }] : []}
              availableCash={availableCashAfterOrders}
              loading={orderDialogLoading}
            />
          )}
          {quickBuyModals}
        </div>
        <div className="watchlist-notes-col">
          <TickerNotesPanel
            selectedItem={readylist.find(w => w.id === selectedId)}
            getTickerIdForItem={getTickerIdForReadylistItem}
            onNoteChange={fetchReadylist}
          />
        </div>
      </div>
    </div>
  );
};

export default Readylist; 
